import type { Map as MapLibreMap } from 'maplibre-gl'
import type * as GeoJSON from 'geojson'
import type { WorkplaceMarker } from './workplaceOverlay'

/** ContactStatus enum values (mirrors backend ContactStatus) */
export const ContactStatus = {
  NotContacted: 0,
  Contacted: 1,
  Interested: 2,
  NotInterested: 3,
  NoAnswer: 4,
  Callback: 5,
} as const

/**
 * Contact marker for map display
 */
export interface ContactMarker {
  id: string
  longitude: number
  latitude: number
  label: string
  streetAddress?: string
  zip?: string
  walkOrder?: number
  isVisited?: boolean
}

/**
 * Household marker for grouped contacts at same address
 */
export interface HouseholdMarker {
  householdKey: string
  longitude: number
  latitude: number
  streetAddress: string
  city: string
  state: string | null
  zip: string
  contactCount: number
  visitedCount: number
  needsRevisitCount: number
  contacts: {
    contactId: string
    contactName: string
    walkOrder: number
    isVisited: boolean
    contactStatus: number
    turfId: string | null
    turfIndex: number
  }[]
}

/**
 * Turf boundary polygon for map display
 */
export interface TurfBoundary {
  id: string
  label: string
  color?: string
  geoJson: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>
}

/**
 * Props for the base TurfMap component
 */
export interface TurfMapProps {
  /** Turf boundary polygons to display */
  boundaries?: TurfBoundary[]

  /** Contact markers to display (with optional clustering) */
  contacts?: ContactMarker[]

  /** Household markers to display (grouped contacts at same address) */
  households?: HouseholdMarker[]

  /** Route line connecting contacts in walk order */
  routeGeoJson?: GeoJSON.Feature<GeoJSON.LineString> | null

  /** Called when a contact marker is clicked */
  onMarkerClick?: (contactId: string) => void

  /** Called when a household marker is clicked */
  onHouseholdClick?: (householdKey: string) => void

  /** Called when a contact within a household popup is clicked */
  onHouseholdContactClick?: (contactId: string) => void

  /** Called when a turf boundary is clicked */
  onBoundaryClick?: (boundaryId: string) => void

  /** Called when the map is fully loaded */
  onMapLoad?: (map: MapLibreMap) => void

  /** Auto-fit map bounds to show all features (default: true) */
  fitToBounds?: boolean

  /** Map container height (default: '500px') */
  height?: string | number

  /** Disable map click interactions (used when drawing tools are active) */
  disableInteractions?: boolean

  /** Workplace markers to display (grouped contacts at same business) */
  workplaces?: WorkplaceMarker[]

  /** Called when a contact within a workplace popup is clicked */
  onWorkplaceContactClick?: (contactId: string) => void

  /** Called when a worksite name in a workplace popup is clicked */
  onWorksiteClick?: (worksiteId: string) => void

  /** [O4] Numbered stops in walk order; add-ins (isAddIn) draw as a violet diamond */
  stops?: GeoJSON.FeatureCollection

  /** [O4] Draw routeGeoJson (default true — callers that never set it keep today's map) */
  showPath?: boolean

  /** [O4] Animate the drive path (skipped under prefers-reduced-motion) */
  animatePath?: boolean

  /** [O4] Suggested stops from the corridor search */
  ghosts?: GeoJSON.FeatureCollection

  /** [O4] Called with the ghost's key (and the click's container-pixel point) when a suggestion is clicked */
  onGhostClick?: (key: string, point?: { x: number; y: number }) => void

  /** [O4] Called with the OpportunisticStop id (and the click's container-pixel point) when an add-in diamond is clicked */
  onAddInClick?: (stopId: string, point?: { x: number; y: number }) => void

  /** Optional children to render inside the Map component (e.g. Marker overlays) */
  children?: React.ReactNode
}

/**
 * Props for TurfDrawingMap (extends TurfMap with drawing capabilities)
 */
export interface TurfDrawingMapProps extends TurfMapProps {
  /** Called when a polygon is drawn and completed */
  onPolygonComplete: (polygon: GeoJSON.Feature<GeoJSON.Polygon>) => void

  /** Called when the clear button is clicked */
  onPolygonClear?: () => void

  /** Enable/disable drawing tools (default: true) */
  drawingEnabled?: boolean
}
